import { useEffect, useState } from "react";
import {
  KeyRound, Copy, Ban, Plus, AlertTriangle, CheckCircle2,
  RefreshCw, Pencil, Zap, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

// Fase M — Panel de "Aplicaciones cliente".
// Una tarjeta por aplicación registrada. Cada aplicación tiene una key
// activa (o dos temporalmente durante una rotación con grace period).
// Nombre del componente y ruta del archivo se mantienen para no forzar
// cambios en la página que lo importa.
export default function GatewayKeyCard({ disabled }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);         // { api_key, record }
  const [rotating, setRotating] = useState(null);         // key row
  const [editing, setEditing] = useState(null);           // key row
  const [showCreate, setShowCreate] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/gateway/keys");
      setKeys(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setLoading(false); return; }
    refresh();
  }, [disabled]);

  const revoke = async (id) => {
    if (!window.confirm(
      "¿Revocar esta API Key? La aplicación dejará de tener acceso inmediatamente. " +
      "Si prefieres renovar sin downtime, usa 'Rotar' en su lugar."
    )) return;
    try {
      await api.post(`/gateway/keys/${id}/revoke`);
      toast.success("Clave revocada");
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  // Hard-delete: removes the key from storage AND the listing. Works
  // on both active and revoked keys. If the key is still active, the
  // backend revokes it first (same transaction) so there is no race
  // window where the key could still authenticate.
  const destroy = async (k) => {
    const last4 = k.last4 || "????";
    const prompt = k.active === false || k.revoked_at
      ? `¿Eliminar permanentemente la clave que termina en …${last4}?`
      : `Esta clave sigue activa (…${last4}). Al eliminarla se revoca Y se borra permanentemente de la lista. La aplicación dejará de tener acceso.`;
    const confirmText = window.prompt(
      `${prompt}\n\nEsta acción NO se puede deshacer. Escribe ELIMINAR para confirmar.`
    );
    if (confirmText !== "ELIMINAR") {
      if (confirmText !== null) toast.info("Eliminación cancelada");
      return;
    }
    try {
      await api.delete(`/gateway/keys/${k.id}`);
      toast.success(`Clave …${last4} eliminada`);
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="rounded-md border border-zinc-800 bg-zinc-950 p-5 space-y-3">
        <div className="text-sm text-zinc-400">
          Crea una <span className="text-white">aplicación cliente</span> (por ejemplo{" "}
          <code className="text-emerald-300">edit-krimagina</code>) y genera su API Key.
          La clave se muestra una única vez: cópiala en ese momento.
          Después solo verás su prefijo y últimos 4 caracteres. Las aplicaciones{" "}
          <span className="text-white">no necesitan conocer</span> las claves de
          OpenAI, Gemini, OpenRouter o cualquier otro proveedor: KRAI CONNECT
          las gestiona por ellas.
        </div>
        <Button
          data-testid={TID.gateway.issueKeyBtn}
          onClick={() => setShowCreate(true)}
          disabled={disabled}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Plus className="h-3.5 w-3.5" />
          Nueva aplicación
        </Button>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-xs text-center py-6">Cargando…</div>
      ) : keys.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-8 text-center text-sm text-zinc-500">
          Aún no has registrado ninguna aplicación cliente.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {keys.map((k) => (
            <AppCard
              key={k.id}
              k={k}
              onRotate={() => setRotating(k)}
              onEdit={() => setEditing(k)}
              onRevoke={() => revoke(k.id)}
              onDelete={() => destroy(k)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateAppDialog
          onClose={() => setShowCreate(false)}
          onCreated={(payload) => {
            setShowCreate(false);
            setRevealed(payload);
            refresh();
          }}
          setCreating={setCreating}
          creating={creating}
        />
      )}

      {rotating && (
        <RotateDialog
          keyRow={rotating}
          onClose={() => setRotating(null)}
          onRotated={(payload) => {
            setRotating(null);
            setRevealed(payload);
            refresh();
          }}
        />
      )}

      {editing && (
        <EditDialog
          keyRow={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {revealed && (
        <RevealDialog revealed={revealed} onClose={() => setRevealed(null)} onCopy={copy} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function AppCard({ k, onRotate, onEdit, onRevoke, onDelete }) {
  const active = k.active !== undefined ? k.active : !k.revoked_at;
  const rotating = !!k.revokes_at && !k.revoked_at;
  return (
    <div
      data-testid={TID.gateway.keyRow(k.id)}
      className="rounded-md border border-zinc-800 bg-zinc-950 p-4 space-y-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-emerald-300 truncate">
            {k.app_id || <span className="text-zinc-500 italic">legacy</span>}
          </div>
          {k.name && (
            <div className="text-xs text-zinc-400 truncate">{k.name}</div>
          )}
          {k.app_url && (
            <a
              href={k.app_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`gateway-app-url-${k.id}`}
              className="text-[11px] text-sky-400 hover:text-sky-300 truncate block font-mono"
            >
              {k.app_url}
            </a>
          )}
        </div>
        <StatusBadge active={active} rotating={rotating} />
      </div>

      <div className="text-[11px] font-mono text-zinc-500">
        {k.prefix}••••••••••••{k.last4}
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-500">
        <div>Creada · <span className="text-zinc-300 font-mono">{fmtDate(k.created_at)}</span></div>
        <div>Último uso · <span className="text-zinc-300 font-mono">{fmtDate(k.last_used_at) || "—"}</span></div>
        {rotating && (
          <div className="col-span-2 text-amber-400">
            Expira · <span className="font-mono">{fmtDate(k.revokes_at)}</span>
          </div>
        )}
      </div>

      <UseCasesRow use_cases={k.use_cases} />
      <CompatibilityRow k={k} />
      <ScopeSummary k={k} />

      <div className="flex flex-wrap gap-2 pt-1">
        {active && (
          <>
            <button
              onClick={onRotate}
              className="text-[11px] px-2 py-1 rounded border border-zinc-800 hover:bg-zinc-900 text-zinc-200 inline-flex items-center gap-1"
              title="Emite una clave nueva visible una sola vez (reemplaza la actual)"
            >
              <RefreshCw className="h-3 w-3" /> Regenerar y ver
            </button>
            <button
              onClick={onEdit}
              className="text-[11px] px-2 py-1 rounded border border-zinc-800 hover:bg-zinc-900 text-zinc-200 inline-flex items-center gap-1"
            >
              <Pencil className="h-3 w-3" /> Editar
            </button>
            <button
              data-testid={TID.gateway.keyRevoke(k.id)}
              onClick={onRevoke}
              className="text-[11px] px-2 py-1 rounded border border-red-900/50 hover:bg-red-950/40 text-red-300 inline-flex items-center gap-1"
            >
              <Ban className="h-3 w-3" /> Revocar
            </button>
          </>
        )}
        {/* Hard-delete: visible for both active and revoked keys so the
            user can clean up the list. Always behind a strong confirm
            prompt that forces typing 'ELIMINAR'. */}
        <button
          data-testid={TID.gateway.keyDelete(k.id)}
          onClick={onDelete}
          title="Eliminar permanentemente"
          className="text-[11px] px-2 py-1 rounded border border-red-900/50 hover:bg-red-950/60 text-red-300 inline-flex items-center gap-1 ml-auto"
        >
          <Trash2 className="h-3 w-3" /> Eliminar
        </button>
      </div>
    </div>
  );
}

function UseCasesRow({ use_cases }) {
  if (!Array.isArray(use_cases) || use_cases.length === 0) return null;
  const byId = Object.fromEntries(USE_CASE_OPTIONS.map(o => [o.id, o.label]));
  return (
    <div className="flex flex-wrap gap-1">
      {use_cases.map((uc) => (
        <span
          key={uc}
          className="text-[10px] px-1.5 py-0.5 rounded font-mono border border-sky-900/60 bg-sky-950/40 text-sky-300"
        >
          {byId[uc] || uc}
        </span>
      ))}
    </div>
  );
}

function CompatibilityRow({ k }) {
  const c = k.compatibility;
  if (!c) return null;
  if (c.status === "unknown") return null;
  const isOk = c.status === "ok";
  const isMissing = c.status === "missing";
  const tone = isOk
    ? "border-emerald-900/60 bg-emerald-950/30 text-emerald-300"
    : isMissing
    ? "border-red-900/60 bg-red-950/30 text-red-300"
    : "border-amber-900/60 bg-amber-950/30 text-amber-300";
  const label = isOk
    ? `Compatible · motores: ${c.matching_engines.join(", ") || "—"}`
    : isMissing
    ? `Sin motor compatible · falta: ${c.missing_use_cases.join(", ")}`
    : `Parcial · falta: ${c.missing_use_cases.join(", ")}`;
  return (
    <div
      data-testid={`gateway-compat-${k.id}`}
      data-status={c.status}
      className={"text-[10px] rounded px-1.5 py-1 border " + tone}
    >
      {label}
    </div>
  );
}

function StatusBadge({ active, rotating }) {
  if (!active) {
    return (
      <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] text-red-400">
        <Ban className="h-3 w-3" /> Revocada
      </span>
    );
  }
  if (rotating) {
    return (
      <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] text-amber-400">
        <RefreshCw className="h-3 w-3" /> En rotación
      </span>
    );
  }
  return (
    <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] text-emerald-400">
      <CheckCircle2 className="h-3 w-3" /> Activa
    </span>
  );
}

function ScopeSummary({ k }) {
  const engines = k.allowed_engines;
  const tasks = k.allowed_tasks;
  const rpm = k.rate_limit?.requests_per_minute;
  const rpd = k.rate_limit?.requests_per_day;
  const chips = [];
  if (engines === null || engines === undefined) chips.push({ label: "todos los motores", tone: "muted" });
  else chips.push({ label: `${engines.length} motor(es)`, tone: "info" });
  if (tasks === null || tasks === undefined) chips.push({ label: "todos los tasks", tone: "muted" });
  else chips.push({ label: `${tasks.length} task(s)`, tone: "info" });
  if (rpm || rpd) {
    chips.push({
      label: `rl ${rpm || "∞"}/min · ${rpd || "∞"}/día`,
      tone: "warn",
    });
  } else {
    chips.push({ label: "sin límite", tone: "muted" });
  }
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((c, i) => (
        <span
          key={i}
          className={
            "text-[10px] px-1.5 py-0.5 rounded font-mono border " +
            (c.tone === "info"
              ? "border-emerald-900/60 bg-emerald-950/40 text-emerald-300"
              : c.tone === "warn"
              ? "border-amber-900/60 bg-amber-950/40 text-amber-300"
              : "border-zinc-800 bg-zinc-900 text-zinc-500")
          }
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Use-cases catalog. Must stay in sync with backend `ENGINE_CAPABILITIES`
// (routers/engine_registry.py). Kept as a local const for readability; a
// mismatch is caught server-side (422 on POST/PATCH) so the app never
// silently persists an unknown use_case.
// ---------------------------------------------------------------------------
const USE_CASE_OPTIONS = [
  { id: "chat",                 label: "Chat" },
  { id: "vision",               label: "Visión (analizar imágenes)" },
  { id: "image_edit",           label: "Edición de imágenes" },
  { id: "image_generation",     label: "Generación de imágenes" },
  { id: "audio_transcription",  label: "Transcripción de audio" },
  { id: "audio_generation",     label: "Generación de audio (TTS)" },
  { id: "video_generation",     label: "Generación de vídeo" },
  { id: "embeddings",           label: "Embeddings" },
  { id: "tool_use",             label: "Tool use / function calling" },
  { id: "code",                 label: "Programación / código" },
];

function UseCasesPicker({ value, onChange, testIdPrefix = "usecase" }) {
  const toggle = (id) => {
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    onChange(next);
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-zinc-400">Uso previsto (opcional)</Label>
      <div className="grid grid-cols-2 gap-1.5 rounded-md border border-zinc-800 bg-zinc-950/60 p-2">
        {USE_CASE_OPTIONS.map((opt) => {
          const checked = value.includes(opt.id);
          return (
            <label
              key={opt.id}
              data-testid={`${testIdPrefix}-usecase-${opt.id}`}
              className={
                "flex items-center gap-2 text-[11px] cursor-pointer rounded px-1.5 py-1 " +
                (checked
                  ? "bg-emerald-950/30 text-emerald-200"
                  : "text-zinc-400 hover:bg-zinc-900")
              }
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt.id)}
                className="accent-emerald-500"
              />
              {opt.label}
            </label>
          );
        })}
      </div>
      <div className="text-[10px] text-zinc-500">
        KRAI comparará estos usos con los motores conectados y te avisará si algo no está cubierto.
      </div>
    </div>
  );
}

function slugify(s) {
  // Deterministic lowercase slug: letters/digits/hyphens only, max 32.
  // Falls back to a timestamp slug if the name has no usable chars so
  // the backend regex ^[a-z0-9-]{2,32}$ always matches.
  const base = (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
  if (base.length >= 2) return base;
  return "app-" + Date.now().toString(36).slice(-6);
}

function CreateAppDialog({ onClose, onCreated, setCreating, creating }) {
  const [name, setName] = useState("");
  const [appUrl, setAppUrl] = useState("");
  const [webTargets, setWebTargets] = useState([]);          // catalog
  const [selectedWTs, setSelectedWTs] = useState([]);        // ids checked
  const [allWTs, setAllWTs] = useState(true);                // "all active"

  // Load active web targets so the user can scope the new API key to
  // specific providers. Only `active=true` targets are offered; the key
  // can be edited later to narrow or widen scope.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/gateway/web-targets");
        const list = (r.data || []).filter((t) => t.active);
        setWebTargets(list);
      } catch (e) {
        // Non-fatal: user can still create the key with "all" scope.
        setWebTargets([]);
      }
    })();
  }, []);

  const toggleWT = (id) => {
    setSelectedWTs((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    const n = name.trim();
    if (n.length < 2) {
      toast.error("Escribe un nombre (mínimo 2 caracteres).");
      return;
    }
    if (appUrl && !/^https?:\/\//.test(appUrl)) {
      toast.error("La URL debe empezar por http:// o https://");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/gateway/keys", {
        app_id: slugify(n),
        name: n,
        app_url: appUrl || "",
        // null = scope ampliado (todos los web targets activos).
        // array no vacío = restringido a esos IDs.
        allowed_web_targets: allWTs ? null : selectedWTs,
      });
      onCreated(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-400" />
            Nueva aplicación cliente
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Dale un nombre y elige qué proveedores Web Target podrá usar.
            La API Key se mostrará una sola vez.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Nombre *</Label>
            <Input
              data-testid={TID.gateway.keyName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="EditFlow Pro"
              className="bg-zinc-900 border-zinc-800"
              autoFocus
            />
            {name.trim().length >= 2 && (
              <div className="text-[10px] text-zinc-500 font-mono">
                app_id autogenerado: <span className="text-zinc-300">{slugify(name.trim())}</span>
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">URL de la aplicación (opcional)</Label>
            <Input
              data-testid="gateway-create-app-url"
              value={appUrl}
              onChange={(e) => setAppUrl(e.target.value)}
              placeholder="https://edit.krimagina.com"
              className="bg-zinc-900 border-zinc-800 font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">
              Proveedores Web Target permitidos
            </Label>
            <div className="rounded border border-zinc-800 bg-zinc-900 p-2 space-y-1.5">
              <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allWTs}
                  onChange={(e) => setAllWTs(e.target.checked)}
                  data-testid="gateway-create-wt-all"
                  className="accent-emerald-500"
                />
                <span>Todos los Web Targets activos (recomendado)</span>
              </label>
              {!allWTs && (
                <div className="pl-5 space-y-1 border-l border-zinc-800 ml-1">
                  {webTargets.length === 0 ? (
                    <div className="text-[11px] text-zinc-500 italic py-1">
                      No hay Web Targets activos. Crea uno primero.
                    </div>
                  ) : (
                    webTargets.map((t) => (
                      <label
                        key={t.id}
                        className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedWTs.includes(t.id)}
                          onChange={() => toggleWT(t.id)}
                          data-testid={`gateway-create-wt-${t.id}`}
                          className="accent-emerald-500"
                        />
                        <span className="font-mono">{t.id}</span>
                        {t.name && (
                          <span className="text-zinc-500">— {t.name}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}
                  className="bg-zinc-900 border-zinc-700">
            Cancelar
          </Button>
          <Button
            size="sm" onClick={submit}
            disabled={creating || name.trim().length < 2 ||
                      (!allWTs && selectedWTs.length === 0)}
            className="bg-white text-black hover:bg-zinc-200"
          >
            {creating ? "Generando…" : "Crear y generar API Key"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RotateDialog({ keyRow, onClose, onRotated }) {
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/gateway/keys/${keyRow.id}/rotate`,
                               { grace_period_hours: Math.max(0, Number(hours) || 0) });
      onRotated({ api_key: r.data.api_key, record: r.data.new_record });
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-amber-400" />
            Rotar API Key de <span className="font-mono">{keyRow.app_id || "?"}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Se generará una nueva clave para la misma aplicación. La actual
            seguirá funcionando durante el periodo de gracia para permitirte
            desplegar sin downtime. Pasado ese tiempo, quedará revocada.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs text-zinc-400">Periodo de gracia (horas)</Label>
          <Input
            type="number" min={0} max={720} value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="bg-zinc-900 border-zinc-800 font-mono"
          />
          <div className="text-[10px] text-zinc-500">
            0 = revoca la clave anterior de inmediato. Por defecto 24h.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}
                  className="bg-zinc-900 border-zinc-700">
            Cancelar
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}
                  className="bg-white text-black hover:bg-zinc-200">
            {busy ? "Rotando…" : "Rotar clave"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ keyRow, onClose, onSaved }) {
  const [name, setName] = useState(keyRow.name || "");
  const [notes, setNotes] = useState(keyRow.notes || "");
  const [appUrl, setAppUrl] = useState(keyRow.app_url || "");
  const [useCases, setUseCases] = useState(keyRow.use_cases || []);
  const [engines, setEngines] = useState((keyRow.allowed_engines || []).join(", "));
  const [tasks, setTasks] = useState((keyRow.allowed_tasks || []).join(", "));
  const [rpm, setRpm] = useState(keyRow.rate_limit?.requests_per_minute || "");
  const [rpd, setRpd] = useState(keyRow.rate_limit?.requests_per_day || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (appUrl && !/^https?:\/\//.test(appUrl)) {
      toast.error("La URL debe empezar por http:// o https://");
      return;
    }
    setBusy(true);
    const body = { name, notes, app_url: appUrl };
    body.use_cases = useCases;
    body.allowed_engines = engines.trim() ? engines.split(",").map(s => s.trim()).filter(Boolean) : [];
    body.allowed_tasks   = tasks.trim() ? tasks.split(",").map(s => s.trim()).filter(Boolean) : [];
    body.rate_limit = {
      requests_per_minute: rpm ? Number(rpm) : null,
      requests_per_day:    rpd ? Number(rpd) : null,
    };
    try {
      await api.patch(`/gateway/keys/${keyRow.id}`, body);
      toast.success("Cambios guardados");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-zinc-300" />
            Editar aplicación <span className="font-mono">{keyRow.app_id || "?"}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Todos los campos son opcionales. Dejar en blanco = sin restricción.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <Label className="text-xs text-zinc-400">Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="bg-zinc-900 border-zinc-800" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Label className="text-xs text-zinc-400">URL de la aplicación</Label>
            <Input
              data-testid="gateway-edit-app-url"
              value={appUrl}
              onChange={(e) => setAppUrl(e.target.value)}
              placeholder="https://edit.krimagina.com"
              className="bg-zinc-900 border-zinc-800 font-mono text-xs"
            />
          </div>
          <UseCasesPicker value={useCases} onChange={setUseCases} testIdPrefix="edit" />
          <div className="grid grid-cols-1 gap-2">
            <Label className="text-xs text-zinc-400">Descripción / notas</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Para qué la vas a usar, entorno, propietario, etc."
                      className="bg-zinc-900 border-zinc-800 text-xs" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Label className="text-xs text-zinc-400">Motores permitidos (coma-separado)</Label>
            <Input value={engines} onChange={(e) => setEngines(e.target.value)}
                   placeholder="gemini, openrouter__openai-gpt-4o-mini"
                   className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Label className="text-xs text-zinc-400">Tasks permitidos (coma-separado)</Label>
            <Input value={tasks} onChange={(e) => setTasks(e.target.value)}
                   placeholder="chat, summarize, translate"
                   className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400 inline-flex items-center gap-1">
                <Zap className="h-3 w-3" /> req/min
              </Label>
              <Input type="number" min={1} value={rpm}
                     onChange={(e) => setRpm(e.target.value)}
                     placeholder="∞"
                     className="bg-zinc-900 border-zinc-800 font-mono" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400 inline-flex items-center gap-1">
                <Zap className="h-3 w-3" /> req/día
              </Label>
              <Input type="number" min={1} value={rpd}
                     onChange={(e) => setRpd(e.target.value)}
                     placeholder="∞"
                     className="bg-zinc-900 border-zinc-800 font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={onClose}
                  className="bg-zinc-900 border-zinc-700">
            Cancelar
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}
                  className="bg-white text-black hover:bg-zinc-200">
            {busy ? "Guardando…" : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealDialog({ revealed, onClose, onCopy }) {
  // URL base pública del Gateway. Deriva del REACT_APP_BACKEND_URL del
  // frontend. En producción quedará como `https://connect.krai.es/api`,
  // en preview como `https://krai-dashboard.preview.emergentagent.com/api`.
  const gatewayUrl =
    (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api/gateway/v1";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={TID.gateway.keyRevealDialog}
                     className="bg-zinc-950 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-400" />
            API Key generada — <span className="font-mono">{revealed?.record?.app_id}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            Cópiala ahora. No se volverá a mostrar completa nunca más.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-amber-900/60 bg-amber-950/30 p-3 flex items-start gap-2 text-xs text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Guárdala en un gestor de secretos. Si la pierdes,
            usa <span className="font-semibold">Regenerar y ver</span> en la tarjeta
            para emitir una clave nueva.
          </div>

          {/* URL base del gateway — la app cliente la usa para apuntar
              sus llamadas (p.ej. OpenAI SDK: base_url = esta URL). */}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">
              URL base del Gateway (Base URL)
            </Label>
            <div className="flex items-center gap-2">
              <div
                data-testid="gateway-reveal-base-url"
                className="flex-1 rounded-md border border-zinc-800 bg-black/60 p-2.5 font-mono text-[11px] text-sky-300 break-all select-all"
              >
                {gatewayUrl}
              </div>
              <Button
                size="sm" variant="outline"
                onClick={() => onCopy(gatewayUrl)}
                className="shrink-0 bg-zinc-900 border-zinc-700"
                title="Copiar URL"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="text-[10px] text-zinc-500 leading-relaxed">
              Configura tu app cliente con esta Base URL + la API Key de abajo.
              Compatible con OpenAI SDK (<span className="font-mono">base_url</span>) y HTTP directo.
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">API Key</Label>
            <div className="rounded-md border border-zinc-800 bg-black/60 p-3 font-mono text-xs text-emerald-300 break-all select-all">
              {revealed?.api_key}
            </div>
            <Button
              data-testid={TID.gateway.keyCopyBtn}
              onClick={() => onCopy(revealed?.api_key)}
              size="sm"
              variant="outline"
              className="gap-2 w-full bg-zinc-900 border-zinc-700"
            >
              <Copy className="h-3.5 w-3.5" /> Copiar clave
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onClose}
            size="sm"
            className="bg-white text-black hover:bg-zinc-200"
          >
            He copiado la clave
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------
function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
